import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import RiskScoreBadge from './RiskScoreBadge';
import {
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';

export interface CopySettings {
  copyAmount: string;
  allocationType: 'fixed' | 'percentage' | 'proportional';
  fixedLot?: string;
  riskMultiplier?: string;
  maxDrawdown: string;
  maxDailyLoss?: string;
  maxOpenTrades?: string;
  copyOpenTrades: boolean;
  reverseCopy?: boolean;
  slippagePips?: string;
  stopCopyLevel?: string;
}

interface CopySettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  traderName: string;
  strategyName: string;
  riskScore: number;
  minCopyAmount: number;
  commissionRate: number;
  settings: CopySettings;
  onSettingsChange: (settings: CopySettings) => void;
  onConfirm: () => void;
  loading?: boolean;
}

const CopySettingsDialog = ({
  open,
  onOpenChange,
  traderName,
  strategyName,
  riskScore,
  minCopyAmount,
  commissionRate,
  settings,
  onSettingsChange,
  onConfirm,
  loading = false,
}: CopySettingsDialogProps) => {
  const updateSetting = <K extends keyof CopySettings>(
    key: K,
    value: CopySettings[K]
  ) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Copy {strategyName}</DialogTitle>
          <DialogDescription>
            Configure copy parameters for {traderName}&apos;s strategy
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/20">
            <div>
              <p className="text-sm font-medium">{traderName}</p>
              <p className="text-xs text-muted-foreground">{strategyName}</p>
            </div>
            <RiskScoreBadge score={riskScore} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Min. Copy Amount</p>
              <p className="font-semibold text-sm">
                ${minCopyAmount.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Commission Rate</p>
              <p className="font-semibold text-sm">{commissionRate}%</p>
            </div>
          </div>

          {/* Investment & Allocation Mode */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="copyAmount">Investment Amount ($) *</Label>
              <Input
                id="copyAmount"
                type="number"
                min={minCopyAmount}
                value={settings.copyAmount}
                onKeyDown={preventInvalidNumberInput}
                onPaste={preventInvalidNumberPaste}
                onChange={e => updateSetting('copyAmount', e.target.value)}
                placeholder={String(minCopyAmount)}
              />
            </div>

            <div className="space-y-2">
              <Label>Lot Mode</Label>
              <Select
                value={settings.allocationType}
                onValueChange={value =>
                  updateSetting(
                    'allocationType',
                    value as CopySettings['allocationType']
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select lot mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="proportional">Proportional Lot</SelectItem>
                  <SelectItem value="fixed">Fixed Lot</SelectItem>
                  <SelectItem value="percentage">
                    Percentage of Balance
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Fixed Lot or Multiplier */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {settings.allocationType === 'fixed' ? (
              <div className="space-y-2">
                <Label htmlFor="fixedLot">Fixed Lot Size</Label>
                <Input
                  id="fixedLot"
                  type="number"
                  step="0.01"
                  value={settings.fixedLot || '0.10'}
                  onKeyDown={preventInvalidNumberInput}
                  onPaste={preventInvalidNumberPaste}
                  onChange={e => updateSetting('fixedLot', e.target.value)}
                  placeholder="0.10"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="riskMultiplier">Risk Multiplier (x)</Label>
                <Input
                  id="riskMultiplier"
                  type="number"
                  step="0.1"
                  value={settings.riskMultiplier || '1.0'}
                  onKeyDown={preventInvalidNumberInput}
                  onPaste={preventInvalidNumberPaste}
                  onChange={e =>
                    updateSetting('riskMultiplier', e.target.value)
                  }
                  placeholder="1.0"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="slippagePips">Max Slippage (Pips)</Label>
              <Input
                id="slippagePips"
                type="number"
                value={settings.slippagePips || '3'}
                onKeyDown={preventInvalidNumberInput}
                onPaste={preventInvalidNumberPaste}
                onChange={e => updateSetting('slippagePips', e.target.value)}
                placeholder="3"
              />
            </div>
          </div>

          {/* Risk Limits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxDrawdown">Max Drawdown (%)</Label>
              <Input
                id="maxDrawdown"
                type="number"
                value={settings.maxDrawdown}
                onKeyDown={preventInvalidNumberInput}
                onPaste={preventInvalidNumberPaste}
                onChange={e => updateSetting('maxDrawdown', e.target.value)}
                placeholder="20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxDailyLoss">Max Daily Loss ($)</Label>
              <Input
                id="maxDailyLoss"
                type="number"
                value={settings.maxDailyLoss || '100'}
                onKeyDown={preventInvalidNumberInput}
                onPaste={preventInvalidNumberPaste}
                onChange={e => updateSetting('maxDailyLoss', e.target.value)}
                placeholder="100"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxOpenTrades">Max Open Trades</Label>
              <Input
                id="maxOpenTrades"
                type="number"
                value={settings.maxOpenTrades || '5'}
                onKeyDown={preventInvalidNumberInput}
                onPaste={preventInvalidNumberPaste}
                onChange={e => updateSetting('maxOpenTrades', e.target.value)}
                placeholder="5"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="stopCopyLevel">
              Stop Copy Level ($ Equity Floor)
            </Label>
            <Input
              id="stopCopyLevel"
              type="number"
              value={settings.stopCopyLevel || ''}
              onKeyDown={preventInvalidNumberInput}
              onPaste={preventInvalidNumberPaste}
              onChange={e => updateSetting('stopCopyLevel', e.target.value)}
              placeholder="Stop copying when account equity falls below this amount"
            />
          </div>

          {/* Toggles */}
          <div className="space-y-3 border-t pt-3">
            <div className="flex items-center justify-between">
              <div>
                <Label
                  htmlFor="copyOpenTrades"
                  className="cursor-pointer font-medium"
                >
                  Copy Existing Open Trades
                </Label>
                <p className="text-xs text-muted-foreground">
                  Copy positions that are already open by the master trader
                </p>
              </div>
              <input
                id="copyOpenTrades"
                type="checkbox"
                checked={settings.copyOpenTrades}
                onChange={e =>
                  updateSetting('copyOpenTrades', e.target.checked)
                }
                className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label
                  htmlFor="reverseCopy"
                  className="cursor-pointer font-medium"
                >
                  Reverse Copy
                </Label>
                <p className="text-xs text-muted-foreground">
                  Automatically execute opposite trades (BUY becomes SELL)
                </p>
              </div>
              <input
                id="reverseCopy"
                type="checkbox"
                checked={settings.reverseCopy || false}
                onChange={e => updateSetting('reverseCopy', e.target.checked)}
                className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading}>
            {loading ? 'Starting Copy...' : 'Confirm & Start Copying'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopySettingsDialog;
